import React, { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { AssignedTo } from "@green0meter/api-client";
import { isEmpty } from "lodash";

import SectionDetail from "./components/SectionDetail";
import { useFormAssignmentStore } from "./FormAssignment.store";
import AssignmentSidePanel from "./components/AssignmentSidePanel";
import {
  FormSectionAssignmentConfigRow,
  getFormAssignmentColumns,
} from "./FormAssignment.columns";
import {
  getRelevantSectionsAndQuestions,
  getSectionSelectionChange,
} from "./FormAssignment.helpers";
import Loader from "./components/Loader";
import { useEntityAssignmentStore } from "./components/AssignmentSidePanel/AssignmentSidePanel.store";

import { Table, TableColumn } from "@components/common";
import { useGetFormAssignmentConfig, useUpdateFormAssignment } from "@api";
import { useUserStore } from "@store/user";
import { OpenRow } from "@components/common/Table";

const FormAssignment: FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const { user } = useUserStore();
  const {
    selectedSections,
    setSelectedSections,
    selectedQuestionCodes,
    setSelectedQuestionCodes,
    sidePanelOpen,
    setSidePanelOpen,
  } = useFormAssignmentStore();
  const { setEntitiesAssignments, setSelectedEntities } = useEntityAssignmentStore();
  const [openedRows, setOpenedRows] = useState<OpenRow[]>([]);
  const { formAssignmentConfig } = useGetFormAssignmentConfig(user, {
    formId,
  });
  const { updateFormAssignmentAsync } = useUpdateFormAssignment(user, formId ?? "");

  const handleGeneralSectionAssignment = async (
    value: AssignedTo,
    section: FormSectionAssignmentConfigRow,
  ): Promise<void> => {
    if (!formId) return;

    if (isEmpty(selectedSections)) {
      // If nothing is selected, assignment applies to the current section only
      await updateFormAssignmentAsync({
        formId,
        assignedTo: value,
        sectionCodes: [section.code],
        questionCodes: [],
      });
    } else {
      const { sectionCodes, questionCodes } = getRelevantSectionsAndQuestions(
        selectedSections,
        selectedQuestionCodes,
        formAssignmentConfig,
      );

      await updateFormAssignmentAsync({
        formId,
        assignedTo: value,
        sectionCodes,
        questionCodes,
      });
    }
  };

  const handleCustomSectionAssignment = (
    section: FormSectionAssignmentConfigRow,
  ): void => {
    const selectedSection = selectedSections.find((s) => s.id === section.code);

    if (!selectedSection) {
      // Add section to selection if not already selected
      handleSectionSelectionChange([
        ...selectedSections.map((s) => s.id),
        section.code,
      ]);
    }

    setSidePanelOpen(true);
  };

  const handleToggleSectionDetail = (
    section: FormSectionAssignmentConfigRow,
  ): void => {
    if (openedRows.find((row) => row.id === section.code)) {
      setOpenedRows((prev) => prev.filter((row) => row.id !== section.code));
    } else if (formId) {
      setOpenedRows((prev) => [
        ...prev,
        {
          id: section.code,
          content: <SectionDetail formId={formId} section={section} />,
        },
      ]);
    }
  };

  const handleCloseAssignmentSidePanel = (): void => {
    if (
      selectedSections.length === 1 &&
      selectedSections.some((s) => !s.indeterminate)
    ) {
      // Deselect section when only one section is selected
      handleSectionSelectionChange([]);
    }
    setSidePanelOpen(false);
  };

  const columns: TableColumn<FormSectionAssignmentConfigRow>[] = useMemo(
    () =>
      getFormAssignmentColumns(
        openedRows,
        selectedSections,
        handleToggleSectionDetail,
        handleGeneralSectionAssignment,
        handleCustomSectionAssignment,
      ),
    [openedRows, selectedSections],
  );

  const handleSectionSelectionChange = (selectedIds: string[]): void => {
    if (!formAssignmentConfig) return;

    const { checkedQuestionCodes, newSelectedSections } = getSectionSelectionChange(
      selectedIds,
      formAssignmentConfig,
      selectedSections,
      selectedQuestionCodes,
    );

    setSelectedQuestionCodes(checkedQuestionCodes);
    setSelectedSections(newSelectedSections);
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      setSelectedSections([]);
      setSelectedQuestionCodes([]);
      setEntitiesAssignments([]);
      setSelectedEntities([]);
    };
  }, []);

  useEffect(() => {
    // Update opened rows content when formAssignmentConfig changes
    if (formId) {
      setOpenedRows(
        openedRows.map((row) => {
          const section = formAssignmentConfig?.formSections.find(
            (s) => s.code === row.id,
          );

          if (!section) return row;

          return {
            ...row,
            content: <SectionDetail formId={formId} section={section} />,
          };
        }),
      );
    }
  }, [formAssignmentConfig]);

  const data = useMemo(
    () =>
      formAssignmentConfig?.formSections.map((s) => ({ ...s, id: s.code })) ?? [],
    [formAssignmentConfig],
  );

  return (
    <>
      <Table<FormSectionAssignmentConfigRow>
        columns={columns}
        data={data}
        enableRowSelection
        onSelectionChange={handleSectionSelectionChange}
        selectedRowIds={selectedSections}
        openRows={openedRows}
      />
      {formId && (
        <AssignmentSidePanel
          formId={formId}
          isOpen={sidePanelOpen}
          onClose={handleCloseAssignmentSidePanel}
        />
      )}
      {formId && <Loader formId={formId} />}
    </>
  );
};

export default FormAssignment;
